import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@monksflow/monks-ui';
import { Check } from 'lucide-react';
import { resolveCategoryIcon, STARTER_CATEGORIES } from '@/entities/taxonomy-category';
import { FormSheet } from '@/shared/ui/FormSheet';
import { STANDARD_FIELD_CATEGORIES } from '../../../TaxonomyDetail/components/FieldLibraryModal/standard-fields';

export interface AddStandardCategoriesModalProps {
  open: boolean;
  /** Category names already in the registry — shown but not selectable. */
  addedNames: Set<string>;
  isSubmitting: boolean;
  onClose: () => void;
  onAdd: (names: string[]) => void;
}

/** How many shipped standard fields belong to a category, as a hint of its purpose. */
const STANDARD_FIELD_COUNT = new Map(
  STANDARD_FIELD_CATEGORIES.map((c) => [c.category, c.fields.length] as const),
);

/** Lower-cased, matching how the backend compares category names. */
const normalize = (name: string) => name.trim().toLowerCase();

/**
 * Pick which standard categories to add.
 *
 * Mirrors {@link AddStandardFieldsModal} deliberately — same grouping, same
 * "Select all" checkbox, same already-added treatment. Unlike that picker
 * everything here starts SELECTED: there are only five, they're the set the
 * shipped field catalog is organised into, and someone opening this dialog after
 * deleting one wants them back rather than wanting to re-pick them.
 */
export function AddStandardCategoriesModal(props: AddStandardCategoriesModalProps) {
  const { t } = useTranslation('knowledge');
  const { open, addedNames, isSubmitting, onClose, onAdd } = props;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const added = useMemo(() => new Set(Array.from(addedNames, normalize)), [addedNames]);
  const available = useMemo(
    () => STARTER_CATEGORIES.filter((c) => !added.has(normalize(c.name))),
    [added],
  );

  useEffect(() => {
    if (open) setSelected(new Set(available.map((c) => c.name)));
    // Only on open: re-running as `available` settles would undo the user's
    // unticking mid-dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const allSelected = available.length > 0 && selected.size === available.length;

  // Functional updater: rapid ticks batch, and rebuilding from a captured
  // `selected` silently drops all but the last. Same reasoning as the parent
  // page's own row toggle and both sibling pickers.
  const toggle = (name: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  return (
    <FormSheet
      className="w-[40rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.addStandardCategoriesModal.addStandardCategories')}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        <p className="text-xs text-muted-foreground">
          {t('taxonomy.addStandardCategoriesModal.groupingsMonksflowOrganisesStandardField')}
        </p>

        {available.length > 0 && (
          <label className="flex cursor-pointer items-center gap-2.5 border-b border-border-light pb-3">
            <Checkbox
              checked={allSelected}
              onCheckedChange={() =>
                setSelected(allSelected ? new Set() : new Set(available.map((c) => c.name)))
              }
              aria-label={t('taxonomy.addStandardCategoriesModal.selectAllCategories')}
            />
            <span className="text-sm font-medium text-foreground">
              {t('taxonomy.addStandardCategoriesModal.selectAllCount', {
                count: available.length,
              })}
            </span>
          </label>
        )}

        {/* No `isSubmitting` spinner: see AddStandardFieldsModal. The sibling
            lists modal DOES gate on `query.isLoading`, correctly — it is the only
            one of the three that fetches its catalogue. */}
        <ul className="flex flex-col gap-1">
          {STARTER_CATEGORIES.map((category) => {
            const alreadyAdded = added.has(normalize(category.name));
            const Icon = resolveCategoryIcon(category.icon);
            const fieldCount = STANDARD_FIELD_COUNT.get(category.name) ?? 0;
            return (
              <li key={category.name}>
                <label
                  className={`flex items-center gap-2.5 rounded-md border border-border-light p-2.5 ${
                    alreadyAdded
                      ? 'opacity-60'
                      : 'cursor-pointer hover:border-primary hover:bg-accent/40'
                  }`}
                >
                  {alreadyAdded ? (
                    <Check className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <Checkbox
                      checked={selected.has(category.name)}
                      onCheckedChange={() => toggle(category.name)}
                      className="shrink-0"
                      aria-label={t('taxonomy.addStandardCategoriesModal.addCategory', {
                        name: category.name,
                      })}
                    />
                  )}
                  <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{category.name}</span>
                      {alreadyAdded && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {t('taxonomy.addStandardCategoriesModal.alreadyAdded')}
                        </span>
                      )}
                    </span>
                    {fieldCount > 0 && (
                      <span className="truncate text-xs text-muted-foreground">
                        {t('taxonomy.addStandardCategoriesModal.standardFieldCount', {
                          count: fieldCount,
                        })}
                      </span>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {available.length === 0 && (
          <p className="rounded-lg border border-border-light bg-muted/40 p-3 text-xs text-muted-foreground">
            {t('taxonomy.addStandardCategoriesModal.yourRegistryAlreadyHasEvery')}
          </p>
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label:
            selected.size === 0
              ? t('taxonomy.addStandardCategoriesModal.selectLeastOne')
              : t('taxonomy.addStandardCategoriesModal.addCategories', { count: selected.size }),
          onClick: () => selected.size > 0 && onAdd(Array.from(selected)),
          disabled: selected.size === 0 || isSubmitting,
          loading: isSubmitting,
        }}
        secondary={{ label: t('taxonomy.addStandardCategoriesModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

AddStandardCategoriesModal.displayName = 'AddStandardCategoriesModal';
